import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
  createDeviceServiceRequest,
  getDeviceServiceRequests,
  type CreateDeviceServiceRequestPayload,
  type CustomerServiceRequestDto,
} from '@/api/service-request.api'
import { resetStore } from '@/store/actions/reset'
import type { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

interface DeviceServiceRequestsState {
  byDevice: Record<string, CustomerServiceRequestDto[]>
  statusByDevice: Record<string, Status>
  latestFetchByDevice: Record<string, string>
  createStatus: Status
}

const initialState: DeviceServiceRequestsState = {
  byDevice: {},
  statusByDevice: {},
  latestFetchByDevice: {},
  createStatus: 'idle',
}

export const fetchDeviceServiceRequests = createAsyncThunk<CustomerServiceRequestDto[], string>(
  'deviceServiceRequests/fetch',
  async (deviceId, { rejectWithValue }) => {
    try {
      return await getDeviceServiceRequests(deviceId)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createDeviceServiceRequestThunk = createAsyncThunk<
  CustomerServiceRequestDto,
  { deviceId: string; payload: CreateDeviceServiceRequestPayload }
>('deviceServiceRequests/create', async ({ deviceId, payload }, { rejectWithValue }) => {
  try {
    return await createDeviceServiceRequest(deviceId, payload)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

const deviceServiceRequestsSlice = createSlice({
  name: 'deviceServiceRequests',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceServiceRequests.pending, (state, action) => {
        state.latestFetchByDevice[action.meta.arg] = action.meta.requestId
        state.statusByDevice[action.meta.arg] = 'loading'
      })
      // GET sprzed wysłania zgłoszenia może wrócić po nim — wygrywa tylko ostatnio zlecone pobranie.
      .addCase(fetchDeviceServiceRequests.fulfilled, (state, action) => {
        if (state.latestFetchByDevice[action.meta.arg] !== action.meta.requestId) return
        state.byDevice[action.meta.arg] = action.payload
        state.statusByDevice[action.meta.arg] = 'succeeded'
      })
      .addCase(fetchDeviceServiceRequests.rejected, (state, action) => {
        if (state.latestFetchByDevice[action.meta.arg] !== action.meta.requestId) return
        state.statusByDevice[action.meta.arg] = 'failed'
      })
      .addCase(createDeviceServiceRequestThunk.pending, (state) => {
        state.createStatus = 'loading'
      })
      .addCase(createDeviceServiceRequestThunk.fulfilled, (state, action) => {
        state.createStatus = 'succeeded'
        const { deviceId } = action.meta.arg
        const list = (state.byDevice[deviceId] ?? []).filter((request) => request.id !== action.payload.id)
        state.byDevice[deviceId] = [action.payload, ...list]
      })
      .addCase(createDeviceServiceRequestThunk.rejected, (state) => {
        state.createStatus = 'failed'
      })
      .addCase(resetStore, () => initialState)
  },
})

export default deviceServiceRequestsSlice.reducer
